import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  NgZone,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Subscription } from 'rxjs';

import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { DocumentViewerCacheService } from '@app/core/services/document-viewer-cache.service';
import {
  getEventTotalBytes,
  sizeExceededMessage,
  sizeExceedsLimit,
} from '@app/work-order/documents/viewer-size-guard.util';

// Draco decoder is fetched lazily from CDN only when a model actually uses Draco-compressed
// geometry — shared across all viewer instances so it's created once.
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');

@Component({
  selector: 'app-model-3d-viewer',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, FormsModule],
  templateUrl: './model-3d-viewer.component.html',
  styleUrls: ['./model-3d-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Model3dViewerComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('rendererCanvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  /** When set, the viewer auto-loads the 3D model from this URL (embedded mode). */
  @Input() src: string | null = null;
  /** Display name shown in the toolbar when loading from a URL. */
  @Input() externalFileName: string | null = null;
  @Input() knownFileType: string | null = null;
  @Input() knownFileSize: number | null = null;

  isLoading = false;
  loadError: string | null = null;
  sizeExceeded = false;
  loadProgress: number | null = null;
  fileName: string | null = null;
  animationNames: string[] = [];
  currentAnimationIndex = 0;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private controls!: OrbitControls;
  private mixer: THREE.AnimationMixer | null = null;
  private clock = new THREE.Clock();
  private animationId: number | null = null;
  private currentModel: THREE.Object3D | null = null;
  private currentAnimations: THREE.AnimationClip[] = [];
  private resizeObserver!: ResizeObserver;
  private loadSubscription: Subscription | null = null;
  private renderLoopPaused = false;
  private readonly onVisibilityChange = () => {
    this.renderLoopPaused = document.hidden;
  };

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private httpClient: HttpClient,
    private viewerCache: DocumentViewerCacheService,
  ) {}

  ngOnInit(): void {
    this.initThreeJs();
    this.startRenderLoop();
    this.watchResize();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    // Auto-load if src was bound before ngOnInit (static binding)
    if (this.src) {
      this.loadFromUrl(this.src);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // After init, react to src changes
    if (changes['src'] && !changes['src'].firstChange && this.renderer) {
      const url = changes['src'].currentValue as string | null;
      if (url) {
        this.loadFromUrl(url);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopRenderLoop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.loadSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.renderer?.dispose();
    this.controls?.dispose();
    this.disposeModel();
  }

  // ──────────────────────────── Three.js init ──────────────────────────────

  private initThreeJs(): void {
    const canvas = this.canvasRef.nativeElement;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    // Cap pixel ratio: rendering at full devicePixelRatio on 3x/4x-DPI displays roughly
    // quadruples GPU/fill-rate cost for negligible visual gain on a model viewer.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.updateRendererSize();

    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1e1e2e);
    this.scene.fog = new THREE.Fog(0x1e1e2e, 50, 200);

    // Camera
    const { width, height } = this.getCanvasSize();
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(0, 10, 30);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(10, 20, 10);
    dirLight.castShadow = true;
    // Lower shadow-map resolution: 1024² is a good quality/perf trade-off for a device viewer
    // (2048² quadruples shadow-pass cost for barely-perceptible sharpness gain).
    dirLight.shadow.mapSize.set(1024, 1024);
    this.scene.add(dirLight);

    const hemiLight = new THREE.HemisphereLight(0xffeeb1, 0x080820, 0.4);
    this.scene.add(hemiLight);

    // Grid helper
    const grid = new THREE.GridHelper(100, 20, 0x444466, 0x333355);
    this.scene.add(grid);

    // Orbit controls
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 500;
  }

  // ──────────────────────────── File input ─────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.loadFile(input.files[0]);
    // reset so the same file can be re-selected
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files[0];
    if (file) this.loadFile(file);
  }

  private loadFile(file: File): void {
    const lower = file.name.toLowerCase();
    const extension = lower.split('.').pop() ?? '';
    if (!['fbx', 'glb', 'gltf'].includes(extension)) {
      this.loadError = 'Only .fbx, .glb, .gltf files are supported.';
      this.cdr.markForCheck();
      return;
    }
    if (sizeExceedsLimit(extension, file.size)) {
      this.handleSizeExceeded(extension, file.size);
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.sizeExceeded = false;
    this.fileName = file.name;
    this.cdr.markForCheck();

    const reader = new FileReader();
    reader.onload = (e) => this.parseModel(e.target?.result as ArrayBuffer, extension);
    reader.onerror = () => {
      this.loadError = 'Failed to read file.';
      this.isLoading = false;
      this.cdr.markForCheck();
    };
    reader.readAsArrayBuffer(file);
  }

  loadFromUrl(url: string): void {
    const fileName = this.externalFileName ?? url.split('/').pop() ?? url;
    const extension = (this.knownFileType || fileName.split('.').pop() || 'fbx').toLowerCase();

    if (this.knownFileSize && sizeExceedsLimit(extension, this.knownFileSize)) {
      this.fileName = fileName;
      this.handleSizeExceeded(extension, this.knownFileSize);
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.sizeExceeded = false;
    this.loadProgress = null;
    this.fileName = fileName;
    this.cdr.markForCheck();

    const cached = this.viewerCache.get(url);
    if (cached) {
      this.parseModel(cached as ArrayBuffer, extension);
      return;
    }

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.httpClient
      .get(url, { responseType: 'arraybuffer', observe: 'events', reportProgress: true })
      .subscribe({
        next: (event) => {
          const totalBytes = getEventTotalBytes(event);
          if (totalBytes && sizeExceedsLimit(extension, totalBytes)) {
            this.loadSubscription?.unsubscribe();
            this.handleSizeExceeded(extension, totalBytes);
            return;
          }
          if (event.type === HttpEventType.DownloadProgress && event.total) {
            this.loadProgress = Math.round((event.loaded / event.total) * 100);
            this.cdr.markForCheck();
          } else if (event.type === HttpEventType.Response && event.body) {
            this.viewerCache.set(url, event.body);
            this.parseModel(event.body, extension);
          }
        },
        error: (err) => {
          this.loadError = `Failed to load model: ${err.message ?? err.status}`;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private handleSizeExceeded(extension: string, totalBytes: number): void {
    this.isLoading = false;
    this.sizeExceeded = true;
    this.loadProgress = null;
    this.loadError = sizeExceededMessage(extension, totalBytes);
    this.cdr.markForCheck();
  }

  private parseModel(buffer: ArrayBuffer, extension: string): void {
    if (extension === 'glb' || extension === 'gltf') {
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);
      loader.parse(
        buffer,
        '',
        (gltf) => this.onModelLoaded(gltf.scene, gltf.animations),
        (err) => this.handleParseError(err as ErrorEvent),
      );
    } else {
      const loader = new FBXLoader();
      try {
        const model = loader.parse(buffer, '');
        this.onModelLoaded(model, model.animations);
      } catch (err) {
        this.handleParseError(err as Error);
      }
    }
  }

  private handleParseError(err: Error | ErrorEvent): void {
    const message = 'message' in err ? err.message : String(err);
    this.loadError = `Failed to parse model: ${message}`;
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private onModelLoaded(model: THREE.Object3D, animations: THREE.AnimationClip[] = []): void {
    this.disposeModel();

    // Scale & shadow
    model.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    // Auto-fit camera to model
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    model.position.sub(center);
    this.scene.add(model);
    this.currentModel = model;
    this.currentAnimations = animations;

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    const cameraDistance = Math.abs(maxDim / Math.sin(fov / 2)) * 0.7;

    this.camera.position.set(cameraDistance * 0.6, cameraDistance * 0.4, cameraDistance * 0.6);
    this.camera.near = cameraDistance / 100;
    this.camera.far = cameraDistance * 10;
    this.camera.updateProjectionMatrix();
    this.controls.target.set(0, 0, 0);
    this.controls.update();

    // Animations
    this.animationNames = animations.map((clip, i) => clip.name || `Animation ${i + 1}`);
    if (animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(model);
      this.playAnimation(0);
    }

    this.isLoading = false;
    this.loadProgress = null;
    this.cdr.markForCheck();
  }

  // ──────────────────────────── Animations ─────────────────────────────────

  playAnimation(index: number): void {
    if (!this.mixer || !this.currentModel) return;
    this.mixer.stopAllAction();
    this.currentAnimationIndex = index;
    const clip = this.currentAnimations[index];
    if (clip) {
      this.mixer.clipAction(clip).play();
    }
    this.cdr.markForCheck();
  }

  // ──────────────────────────── Render loop ────────────────────────────────

  private startRenderLoop(): void {
    this.ngZone.runOutsideAngular(() => {
      const loop = () => {
        this.animationId = requestAnimationFrame(loop);
        // Skip rendering while the browser tab is hidden — saves CPU/GPU/battery without
        // affecting perceived load time (nothing is visible anyway).
        if (this.renderLoopPaused) return;
        const delta = this.clock.getDelta();
        this.mixer?.update(delta);
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
      };
      loop();
    });
  }

  private stopRenderLoop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // ──────────────────────────── Helpers ────────────────────────────────────

  private watchResize(): void {
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.canvasRef.nativeElement.parentElement!);
  }

  private onResize(): void {
    this.updateRendererSize();
    const { width, height } = this.getCanvasSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private updateRendererSize(): void {
    const { width, height } = this.getCanvasSize();
    this.renderer.setSize(width, height, false);
  }

  private getCanvasSize(): { width: number; height: number } {
    const el = this.canvasRef.nativeElement.parentElement!;
    return { width: el.clientWidth || 800, height: el.clientHeight || 600 };
  }

  private disposeModel(): void {
    if (this.currentModel) {
      this.mixer?.stopAllAction();
      this.mixer = null;
      this.scene.remove(this.currentModel);
      this.currentModel.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.geometry?.dispose();
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            (mesh.material as THREE.Material)?.dispose();
          }
        }
      });
      this.currentModel = null;
    }
    this.animationNames = [];
    this.currentAnimations = [];
    this.currentAnimationIndex = 0;
  }

  resetCamera(): void {
    this.camera.position.set(0, 10, 30);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }
}
